import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import type { IImmunizationRecord } from 'src/interfaces/family'
import { exemptionReasonOptions } from 'src/config/options'

dayjs.extend(isSameOrBefore)

export type ImmunizationRecordStatus =
  | 'Complete'
  | 'Overdue'
  | 'Exempt'
  | 'Missing Info'

interface ImmunizationRecordStatusMeta {
  label: ImmunizationRecordStatus
  color: string
  textColor: string
  icon: string
}

const statusMeta: Record<ImmunizationRecordStatus, ImmunizationRecordStatusMeta> =
  {
    Complete: {
      label: 'Complete',
      color: 'positive',
      textColor: 'white',
      icon: 'check_circle',
    },
    Overdue: {
      label: 'Overdue',
      color: 'negative',
      textColor: 'white',
      icon: 'error',
    },
    Exempt: {
      label: 'Exempt',
      color: 'orange',
      textColor: 'white',
      icon: 'privacy_tip',
    },
    'Missing Info': {
      label: 'Missing Info',
      color: 'grey-5',
      textColor: 'white',
      icon: 'help_outline',
    },
  }

function deriveImmunizationRecordStatus(
  record: IImmunizationRecord,
): ImmunizationRecordStatus {
  if (
    record.exemptionReason &&
    exemptionReasonOptions.includes(record.exemptionReason)
  ) {
    return 'Exempt'
  }

  const nextDueDate = record.nextDueDate

  if (nextDueDate && nextDueDate !== 'N/A') {
    const parsedNextDueDate = dayjs(nextDueDate)
    if (
      parsedNextDueDate.isValid() &&
      parsedNextDueDate.isSameOrBefore(dayjs(), 'day')
    ) {
      return 'Overdue'
    }
  }

  if (record.lastDoseDate && nextDueDate) {
    if (nextDueDate === 'N/A') {
      return 'Complete'
    }

    const parsedNextDueDate = dayjs(nextDueDate)
    if (
      parsedNextDueDate.isValid() &&
      parsedNextDueDate.isAfter(dayjs(), 'day')
    ) {
      return 'Complete'
    }
  }

  return 'Missing Info'
}

export function getImmunizationRecordStatus(
  record: IImmunizationRecord,
): ImmunizationRecordStatus {
  return deriveImmunizationRecordStatus(record)
}

export function getImmunizationRecordStatusMeta(
  record: IImmunizationRecord,
): ImmunizationRecordStatusMeta {
  return statusMeta[getImmunizationRecordStatus(record)]
}

export function shouldShowImmunizationExpiryAlert(
  record: IImmunizationRecord,
) {
  const status = getImmunizationRecordStatus(record)
  return (
    (status === 'Complete' || status === 'Overdue') &&
    !!record.nextDueDate &&
    record.nextDueDate !== 'N/A'
  )
}
